import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import * as z from 'zod'
import { useAuth } from '@/hooks/getAuth'
import { useMask } from '@react-input/mask'
import { useMutation } from '@tanstack/react-query'
import { enterpriseSalesInquiryEnterpriseSalesInquiryPostMutation } from '@/generated/@tanstack/react-query.gen'
import LoadingAnimate from '../loading-animate'
import Spinner from '../Spinner'

interface Props {
  open: boolean
  onSubmittedClose: () => void
  onClose: () => void
}

const phoneRegex = /^\+1\s\(\d{3}\)\s\d{3}-\d{4}$/
const formSchema = z.object({
  fullName: z.string(),
  email: z.string(),
  company: z.string().min(2, 'Company name must be at least 2 characters'),
  phone: z
    .string()
    .min(1, 'Phone number is required')
    .regex(phoneRegex, 'Please enter a valid phone number'),
})

export default function TrialContactModal({
  open,
  onSubmittedClose,
  onClose,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        showClose={true}
        className="!p-8 rounded-lg max-w-4xl max-h-[80vh] md:gap-6"
      >
        <TrialContactContent
          open={open}
          onSubmittedClose={onSubmittedClose}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  )
}

const TrialContactContent = ({ onSubmittedClose }: Props) => {
  const [submitted, setSubmitted] = useState(false)

  const { mutate, isPending } = useMutation({
    ...enterpriseSalesInquiryEnterpriseSalesInquiryPostMutation({}),
    onSuccess: () => setSubmitted(true),
  })

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    mutate({
      body: {
        name: values.fullName,
        email: values.email,
        company: values.company,
        phone: values.phone,
      },
    })
  }
  if (submitted) {
    return <ContactSubmitted onClose={onSubmittedClose} />
  }
  return <ContactSubmitForm onSubmit={onSubmit} isPending={isPending} />
}

const ContactSubmitForm = ({
  onSubmit,
  isPending,
}: {
  onSubmit: (values: z.infer<typeof formSchema>) => void
  isPending: boolean
}) => {
  const { auth } = useAuth()

  const phoneNumberMask = {
    mask: '+1 (___) ___-____',
    replacement: { _: /\d/ },
  }
  const phoneInputRef = useMask(phoneNumberMask)
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: '',
      email: '',
      company: '',
      phone: '',
    },
  })

  useEffect(() => {
    if (auth) {
      form.setValue('fullName', auth.name)
      form.setValue('email', auth.email)
    }
  }, [auth])

  return (
    <>
      <DialogHeader>
        <DialogTitle className="md:text-[32px] flex items-center gap-4">
          Enterprise Inquiry
        </DialogTitle>
      </DialogHeader>
      {isPending && (
        <div className="flex items-center justify-center">
          <Spinner />
        </div>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="fullName"
              disabled={isPending}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Full Name <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your full name"
                      className="h-14"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              disabled={true}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Email <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your email"
                      type="email"
                      className="h-14"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="company"
              disabled={isPending}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Company <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter your company name"
                      className="h-14"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phone"
              disabled={isPending}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Phone Number <span className="text-red-500">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      ref={phoneInputRef}
                      type="tel"
                      placeholder="Enter your phone number"
                      className="h-14"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <Button type="submit" className="w-[154px] h-14">
            Submit
          </Button>
        </form>
      </Form>
    </>
  )
}

const ContactSubmitted = ({ onClose }: { onClose: () => void }) => {
  return (
    <div className="flex flex-col items-center justify-center gap-6 p-8">
      <svg
        width="96"
        height="96"
        viewBox="0 0 96 96"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clip-path="url(#clip0_6421_410)">
          <path
            d="M68.3996 41.0998C68.946 40.534 69.2484 39.7761 69.2416 38.9896C69.2348 38.203 68.9193 37.4505 68.363 36.8943C67.8068 36.3381 67.0544 36.0226 66.2678 36.0157C65.4812 36.0089 64.7234 36.3113 64.1576 36.8578L42.2576 58.7578L32.3576 48.8578C31.7918 48.3113 31.034 48.0089 30.2474 48.0157C29.4608 48.0226 28.7083 48.3381 28.1521 48.8943C27.5959 49.4505 27.2804 50.203 27.2736 50.9896C27.2667 51.7761 27.5691 52.534 28.1156 53.0998L40.1156 65.0998C40.6782 65.6622 41.4411 65.9781 42.2366 65.9781C43.0321 65.9781 43.795 65.6622 44.3576 65.0998L68.3576 41.0998H68.3996Z"
            fill="#00AC9C"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M48 0C21.48 0 0 21.48 0 48C0 74.52 21.48 96 48 96C74.52 96 96 74.52 96 48C96 21.48 74.52 0 48 0ZM6 48C6 24.78 24.78 6 48 6C71.22 6 90 24.78 90 48C90 71.22 71.22 90 48 90C24.78 90 6 71.22 6 48Z"
            fill="#00AC9C"
          />
        </g>
        <defs>
          <clipPath id="clip0_6421_410">
            <rect width="96" height="96" fill="white" />
          </clipPath>
        </defs>
      </svg>
      <h2 className="text-2xl font-bold text-center text-[#202224]">
        Contact Information Submitted
      </h2>
      <p className="text-base text-center max-w-md">
        Thank you for your interest in Hydra Score for Enterprise.
      </p>
      <p className="text-base text-center max-w-md">
        Someone from our team will reach out within 1 business day.
      </p>
      <Button onClick={onClose}>Close</Button>
    </div>
  )
}
